import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getHotArticles, getLatestArticles } from '@/mocks/articles';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const hotArticles = getHotArticles();
  const latestArticles = getLatestArticles();
  const filteredArticles = activeCategory === 'all'
    ? latestArticles
    : latestArticles.filter((a) => a.categorySlug === activeCategory);

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51吃瓜网',
    url: 'https://51chigua.com',
    description: '51吃瓜网今日吃瓜资源提供51吃瓜官网全网最新入口、轻量版和APP客户端下载，随时随地获取最新吃瓜内容。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://51chigua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51吃瓜网',
    url: 'https://51chigua.com',
    description: '全网最新最热的吃瓜资讯平台',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <main>
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[500px] md:min-h-[620px] flex items-end pb-12 md:pb-16">
          <img
            src="https://readdy.ai/api/search-image?query=A%20dramatic%20artistic%20abstract%20background%20with%20warm%20red%20orange%20and%20golden%20gradients%2C%20soft%20flowing%20shapes%2C%20modern%20minimal%20design%2C%20luxurious%20texture%2C%20dark%20edges%20fading%20to%20warm%20center%2C%20cinematic%20atmosphere%2C%20no%20text%2C%20no%20people%2C%20elegant%20art%20piece&width=1600&height=700&seq=hero-bg-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

          <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-6 z-10">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 leading-tight">
                今日吃瓜，<br />从这里开始
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-lg leading-relaxed">
                全网最新最热的吃瓜资讯平台。娱乐圈八卦、网红动态、社会热点一网打尽，每日更新，随时随地获取最新吃瓜内容。
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  href="#hot-today"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  热门吃瓜
                </a>
                <a
                  href="#latest"
                  className="px-6 py-2.5 rounded-full bg-white/15 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
                >
                  最新资讯
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Hot Today */}
        <section id="hot-today" className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                  今日热门
                </h2>
                <p className="text-sm text-foreground-500 mt-1">🔥 大家都在看的吃瓜热点</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {hotArticles.slice(0, 3).map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant={index === 0 ? 'featured' : 'default'}
                />
              ))}
            </div>

            {hotArticles.length > 3 && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {hotArticles.slice(3).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Latest Articles with Category Filter */}
        <section id="latest" className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                  最新吃瓜
                </h2>
                <p className="text-sm text-foreground-500 mt-1">📰 实时更新，不错过任何一个瓜</p>
              </div>
              <Link
                to={`/category/${activeCategory === 'all' ? 'entertainment' : activeCategory}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1 align-middle"></i>
              </Link>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200/70 hover:border-primary-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredArticles.slice(0, 8).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-8">
              关于51吃瓜网
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                {
                  q: '51吃瓜网是什么网站？',
                  a: '51吃瓜网是一个专注于娱乐八卦、网红动态、社会热点等内容聚合的中文资讯平台，致力于为用户提供最新、最全、最热的"吃瓜"内容，每日持续更新。',
                },
                {
                  q: '如何在手机上使用51吃瓜网？',
                  a: '51吃瓜网提供手机端网页版和轻量版APP客户端下载，用户可以通过手机浏览器直接访问官网入口，也可以下载轻量版APP获得更流畅的阅读体验。网页版针对移动端进行了深度优化，确保在手机上也能稳定访问。',
                },
                {
                  q: '51吃瓜网的内容多久更新一次？',
                  a: '51吃瓜网的内容团队全天候监测全网热点，确保在热点事件发生后的最短时间内为您带来最新报道。热门内容实时更新，一般性内容每日更新，保证您不错过任何一个"瓜"。',
                },
                {
                  q: '51吃瓜网的内容是否可靠？',
                  a: '51吃瓜网聚合全网多源信息，力求提供客观、全面的资讯内容。我们建议读者理性看待娱乐资讯，将本站内容作为娱乐参考，不做任何投资和决策依据。',
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 cursor-pointer"
                >
                  <summary className="px-5 py-4 flex items-center justify-between font-medium text-foreground-950 text-sm md:text-base list-none">
                    {faq.q}
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}